// ═══════════════════════════════════════════════════════════════════════════
// generatePriceListPDF — extraído VERBATIM de index.html v0.9.16.
//
// Único cambio respecto del original: la instanciación del documento pasa a
// inyectarse (crearDoc), para poder grabar los comandos de dibujo en pruebas.
// Ni una coordenada, ni una fuente, ni un color fueron tocados.
// ═══════════════════════════════════════════════════════════════════════════

import { LOGO_BASE64 } from "./logo.js";
import { guardarPDF } from "./guardar.js";
import { fmt } from "./comunes.js";

function crearDocPredeterminado() {
  const { jsPDF } = window.jspdf;
  return new jsPDF({ unit: "mm", format: "letter" });
}

export function generatePriceListPDF(client, products, getPrice, discount, crearDoc = crearDocPredeterminado) {
  const doc = crearDoc();
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const ml = 18, mr = pw - 18;
  const contentW = mr - ml;
  const pageBottom = ph - 16;
  let y = 18;

  // Brand palette (matches the Vinícola Total logo)
  const coral = [240, 93, 73];
  const dark = [40, 40, 40];
  const gray = [120, 120, 120];
  const note = [150, 110, 95];
  const faint = [225, 222, 215];

  const hline = (yy, color, w) => {
    doc.setDrawColor(...(color || faint));
    doc.setLineWidth(w || 0.3);
    doc.line(ml, yy, mr, yy);
  };
  const footer = () => {
    doc.setFont(undefined, "normal");
    doc.setFontSize(7);
    doc.setTextColor(190, 190, 190);
    doc.text("Vinícola Total · Lista de Precios · " + new Date().toLocaleDateString("es-MX"), pw / 2, ph - 8, { align: "center" });
  };

  // Photo column
  const photoW = 30, photoH = 40, gap = 7;
  const colTextX = ml + photoW + gap;
  const colTextW = mr - colTextX;
  const lineH = 4.4;

  // ── Header ──
  try { doc.addImage(LOGO_BASE64, "JPEG", ml, y, 16, 16); } catch (e) {}
  doc.setFont(undefined, "bold");
  doc.setFontSize(19);
  doc.setTextColor(...coral);
  doc.text("VINÍCOLA TOTAL", ml + 20, y + 7);
  doc.setFont(undefined, "normal");
  doc.setFontSize(9);
  doc.setTextColor(...gray);
  doc.text("Lista de precios personalizada", ml + 20, y + 13);
  y += 21;
  hline(y, coral, 0.6);
  y += 8;

  // Client block
  doc.setFont(undefined, "bold");
  doc.setFontSize(8);
  doc.setTextColor(...coral);
  doc.text("PREPARADA PARA", ml, y);
  y += 6;
  doc.setFont(undefined, "bold");
  doc.setFontSize(14);
  doc.setTextColor(...dark);
  doc.text(client.name || "Cliente", ml, y);
  doc.setFont(undefined, "normal");
  doc.setFontSize(9);
  doc.setTextColor(...gray);
  doc.text(new Date().toLocaleDateString("es-MX", { year: "numeric", month: "long", day: "numeric" }), mr, y, { align: "right" });
  y += 5;
  if (discount > 0) {
    doc.setFont(undefined, "bold");
    doc.setFontSize(9);
    doc.setTextColor(70, 150, 90);
    doc.text("Descuento especial: " + discount + "%", mr, y, { align: "right" });
    y += 4;
  }
  y += 3;
  hline(y);
  y += 9;

  // ── Products ──
  const activeProducts = products.filter(p => p.category !== "cortesia" && p.grossPrice > 0);
  const categories = [...new Set(activeProducts.map(p => p.category))];
  const catLabels = { tinto: "Vinos Tintos", blanco: "Vinos Blancos", naranja: "Vino Naranja", vermu: "Vermús", cerveza: "Cervezas", destilado: "Destilados", otro: "Otros" };

  const drawParagraph = (text, x, w, startY, size, color, style) => {
    doc.setFont(undefined, style || "normal");
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(text, w);
    let yy = startY;
    for (const ln of lines) {
      if (yy > pageBottom) { footer(); doc.addPage(); yy = 18; }
      doc.text(ln, x, yy);
      yy += lineH;
    }
    return yy;
  };

  for (const cat of categories) {
    const catProds = activeProducts.filter(p => p.category === cat);
    if (catProds.length === 0) continue;

    if (y > pageBottom - 38) { footer(); doc.addPage(); y = 18; }
    doc.setFont(undefined, "bold");
    doc.setFontSize(13);
    doc.setTextColor(...coral);
    doc.text((catLabels[cat] || cat).toUpperCase(), ml, y);
    y += 2.5;
    hline(y, coral, 0.4);
    y += 8;

    for (const p of catProds) {
      const hasImg = !!p.imageBase64;
      const tx = hasImg ? colTextX : ml;
      const tw = hasImg ? colTextW : contentW;

      const price = getPrice(p.id);
      const ieps = p.iepsRate || 26.5;
      const ivaRate = client.ivaRate || 16;
      const netPrice = price * (1 + ieps / 100) * (1 + ivaRate / 100);
      const discountedNet = netPrice * (1 - discount / 100);

      // measure block height (so we never cut a product across a page break)
      doc.setFontSize(12.5);
      const nameLinesN = doc.splitTextToSize(p.name, tw).length;
      doc.setFontSize(9);
      const descLines = p.description ? doc.splitTextToSize(p.description, tw).length : 0;
      const noteLines = p.tastingNotes ? doc.splitTextToSize("Notas de cata: " + p.tastingNotes, tw).length : 0;
      const textH = 4 + nameLinesN * 5.2 + 0.5 + 6.5 + descLines * lineH + (descLines ? 2 : 0) + noteLines * lineH + (noteLines ? 2 : 0);
      const blockH = Math.max(hasImg ? photoH : 0, textH);
      const estTotal = blockH + 8;
      if (y + estTotal > pageBottom && estTotal <= pageBottom - 18) { footer(); doc.addPage(); y = 18; }

      const y0 = y;

      // photo (left)
      if (hasImg) {
        try {
          const imgFmt = p.imageBase64.startsWith("data:image/png") ? "PNG" : "JPEG";
          // La foto se ENCAJA respetando su proporción, centrada en el hueco.
          // Antes se estiraba a photoW × photoH: una botella es alta y angosta,
          // así que en una caja casi cuadrada salía achatada — gorda y chaparra
          // en la lista que ve el cliente.
          //
          // `getImageProperties` es de jsPDF 2.x; si no estuviera, se dibuja
          // como antes en vez de quedarse sin foto.
          let ix = ml, iy = y0, iw = photoW, ih = photoH;
          if (typeof doc.getImageProperties === "function") {
            const props = doc.getImageProperties(p.imageBase64);
            if (props?.width > 0 && props?.height > 0) {
              const escala = Math.min(photoW / props.width, photoH / props.height);
              iw = props.width * escala;
              ih = props.height * escala;
              ix = ml + (photoW - iw) / 2;
              iy = y0 + (photoH - ih) / 2;
            }
          }
          doc.addImage(p.imageBase64, imgFmt, ix, iy, iw, ih);
          // El marco se queda en el hueco completo, no en la foto: así los
          // renglones siguen alineados aunque las fotos tengan proporciones
          // distintas.
          doc.setDrawColor(...faint);
          doc.setLineWidth(0.3);
          doc.rect(ml, y0, photoW, photoH);
        } catch (e) { /* una foto ilegible no debe tumbar la lista entera */ }
      }

      // name (full width, wraps freely — no truncation)
      let ty = y0 + 4;
      doc.setFont(undefined, "bold");
      doc.setFontSize(12.5);
      doc.setTextColor(...dark);
      const nameLines = doc.splitTextToSize(p.name, tw);
      for (const nl of nameLines) { doc.text(nl, tx, ty); ty += 5.2; }
      ty += 0.5;

      // price line: price + tax note (left) and stock (right)
      doc.setFont(undefined, "bold");
      doc.setFontSize(13);
      doc.setTextColor(...coral);
      const priceStr = fmt(discountedNet);
      doc.text(priceStr, tx, ty);
      const priceW = doc.getTextWidth(priceStr);
      doc.setFont(undefined, "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(...gray);
      let info = "IVA " + ivaRate + "% + IEPS " + ieps + "% incl.";
      if (discount > 0) info = "antes " + fmt(netPrice) + "   ·   " + info;
      doc.text(info, tx + priceW + 4, ty - 0.3);
      if ((p.stock || 0) > 0) { doc.setTextColor(70, 150, 90); } else { doc.setTextColor(180, 90, 90); }
      doc.setFontSize(8);
      doc.text((p.stock || 0) > 0 ? p.stock + " disponibles" : "Agotado", mr, ty, { align: "right" });
      ty += 6.5;

      // full-width description + tasting notes (no truncation)
      if (p.description) { ty = drawParagraph(p.description, tx, tw, ty, 9, gray, "normal"); ty += 2; }
      if (p.tastingNotes) { ty = drawParagraph("Notas de cata: " + p.tastingNotes, tx, tw, ty, 9, note, "italic"); ty += 2; }

      const bottom = Math.max(hasImg ? y0 + photoH : y0, ty);
      y = bottom + 4;
      hline(y, faint, 0.15);
      y += 6;
    }
    y += 2;
  }

  footer();
  const fileName = "ListaPrecios_" + (client.name || "").replace(/[^a-zA-Z0-9áéíóúñÁÉÍÓÚÑ ]/g, "").replace(/ +/g, "_").substring(0, 30) + ".pdf";
  guardarPDF(doc, fileName);
}
